import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class GoogleAuthService {
  /** The HTTP client */
  private readonly _http = inject(HttpClient);
  /** The Google provider */
  private readonly GOOGLE_PROVIDER = "Google";

  /**
   * Starts the Google sign-in flow
   */
  protected _signInWithGoogle(
    cognitoRegion: string,
    cognitoUserPoolId: string,
    cognitoAuthDomain: string,
  ): void {
    
    const authDomain = `${cognitoAuthDomain}.auth.${cognitoRegion}.amazoncognito.com`;
    const redirectUri = this.getRedirectUri();
    const clientId = cognitoUserPoolId;
    const responseType = "code";
    const scope = encodeURIComponent("email openid profile");
    const googleAuthUrl = `https://${authDomain}/oauth2/authorize?identity_provider=${this.GOOGLE_PROVIDER}&redirect_uri=${redirectUri}&response_type=${responseType}&client_id=${clientId}&scope=${scope}`;
    
    
    window.location.href = googleAuthUrl;
  }

  /**
   * Handles the callback after Google login
   * Must be called from the callback page to which the user is redirected after Google login
   * @param code The authorization code returned by Cognito after Google login
   * @param clientId The client ID of the application
   * @param region The region of the Cognito user pool
   * @param authDomain The auth domain of the Cognito user pool
   * @returns The access token and ID token
   */
  protected async _fetchGoogleTokens(
    code: string,
    clientId: string,
    region: string,
    authDomain: string,
  ): Promise<any> {
    
    const tokenEndpoint = `https://${authDomain}.auth.${region}.amazoncognito.com/oauth2/token`;
    const redirectUri = this.getRedirectUri();
    const headers = new HttpHeaders().set(
      "Content-Type",
      "application/x-www-form-urlencoded",
    );
    const body = new URLSearchParams();
    body.append("grant_type", "authorization_code");
    body.append("client_id", clientId);
    body.append("code", code);
    body.append("redirect_uri", redirectUri);


    try {
      const startTime = performance.now();
      const response = await firstValueFrom(
        this._http.post<any>(tokenEndpoint, body.toString(), { headers }),
      );
      const endTime = performance.now();
      
      return response;
    } catch (error) {
      console.error("❌ [GoogleAuthService] Token request failed!");
      console.error("🔥 [GoogleAuthService] Error details:", error);
      console.error("📊 [GoogleAuthService] Error status:", (error as any)?.status);
      console.error("📜 [GoogleAuthService] Error message:", (error as any)?.message);
      console.error("📄 [GoogleAuthService] Error body:", (error as any)?.error);
      throw new Error(`Error fetch tokens: ${error}`);
    }
  }

  /**
   * Gets the appropriate redirect URI based on the current environment
   */
  private getRedirectUri(): string {
    const protocol = window.location.protocol; // 'http:' o 'https:'
    const hostname = window.location.hostname;
    const port = window.location.port ? `:${window.location.port}` : "";

    const redirectUri = `${protocol}//${hostname}${port}/google-auth-callback`;
    return redirectUri;
  }
}
